import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '@/shared/session/useSession';
import { ChangePasswordPage } from './ChangePasswordPage';

/** Signed-out visitors go to the login page and come back to where they were. The API enforces this too. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();

  if (session.isPending) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }} aria-busy="true" aria-label="Checking your session">
        <CircularProgress />
      </Box>
    );
  }
  if (session.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => { void session.refetch(); }}>Try again</Button>}>
          Could not reach the server to check your session.
        </Alert>
      </Box>
    );
  }
  if (!session.data) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={next === '/' ? '/login' : `/login?next=${encodeURIComponent(next)}`} replace />;
  }
  // A temporary password from an admin: nothing else opens until the person sets their own (the API agrees).
  if (session.data.mustChangePassword) return <ChangePasswordPage />;
  return children;
}
